import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import {
  acceptArenaInvitation,
  subscribePendingArenaInvitationsForUser,
} from "../../../services/arenaInvitationService";
import styles from "../styles/playerExperience.module.css";

/**
 * Home card for a pending referee invitation. Accepting opens the arena as referee.
 */
export default function RefereeInviteBanner() {
  const { uid } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!uid) {
      setInvites([]);
      return undefined;
    }
    return subscribePendingArenaInvitationsForUser(uid, (rows) => {
      setInvites((rows || []).filter((inv) => inv.kind === "referee"));
    });
  }, [uid]);

  if (!invites.length) return null;

  const open = async (inv) => {
    setBusyId(inv.invitationId);
    setError("");
    try {
      const result = await acceptArenaInvitation(inv.invitationId);
      if (!result?.ok) {
        setError("Could not open that tournament.");
        return;
      }
      navigate(result.sessionPath || `/room/${encodeURIComponent(result.room)}/session?tab=overview`);
    } catch (err) {
      setError(err?.message || "Could not open that tournament.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className={styles.bannerStack} aria-label="Referee invitations">
      {invites.map((inv) => (
        <button
          key={inv.invitationId}
          type="button"
          className={styles.drawBannerButton}
          disabled={busyId === inv.invitationId}
          onClick={() => void open(inv)}
          data-testid="home-referee-invite"
        >
          <span className={styles.badge}>Referee invite</span>
          <span className={styles.bannerTournament}>
            You&apos;re invited to referee {inv.arenaName || "a tournament"}
          </span>
          <span className={styles.bannerDetail}>
            {busyId === inv.invitationId ? "Opening the arena…" : "Open the arena and manage matches"}
          </span>
          <span className={styles.bannerCta}>Enter as referee</span>
        </button>
      ))}
      {error ? <p className={styles.muted}>{error}</p> : null}
    </section>
  );
}
